import React from 'react'
import { observer } from 'mobx-react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardHeaderActions,
} from 'components/Card'
import { CapturedInformationTab as Tab } from '../DetailsView/DetailsViewModel'
import styles from './CapturedInformationCard.sass'
import { QuestionDTO } from 'jobs/dtos/questions'
import { QuestionViewModelFor } from 'jobs/questions/type-registry'
import ResponseValue from 'jobs/components/ResponseValue'
import { TabBar, TabItem } from 'components/TabBar'
import { match } from 'utils/pattern-match-util'
import { JobCardEmptyState } from '../JobCardEmptyState'
import QuestionListViewModel from 'jobs/questions/QuestionListViewModel'
import JobViewModel from '../JobViewModel'
import { SectionError, SectionLoading } from '../Section/Section'
import Icon from 'components/Icon'
import Tooltip from 'components/Tooltip'
import { IconButton } from 'components/IconButton'
import { FormGroupContainer } from '../../../../../components/inputs'
import TextArea from '../../../../../components/inputs/TextArea'
import NotesViewModel from './NotesViewModel'
import Button from '../../../../../components/Button'
import NotesDialog from './NotesDialog'
import BusinessInfo from 'jobs/info-gathering/business-info/components/BusinessInfo'
import PersonalProfile from 'jobs/info-gathering/individual-info/components/PersonalProfile'
import SpousalProfile from 'jobs/info-gathering/individual-info/components/SpousalProfile'
import DependentsProfile from 'jobs/info-gathering/individual-info/components/DependentsProfile'
import RefundInfo from 'jobs/info-gathering/tax-refund-method/components/RefundInfo'

interface CapturedInformationProps {
  vm: JobViewModel
}

export default observer(function CapturedInformationCard({
  vm,
}: CapturedInformationProps) {
  React.useEffect(() => {
    // Always show the card by default when the component is first mounted
    vm.detailsVM.responsesSection.set(true)
  }, [vm.detailsVM.responsesSection])

  return (
    <Card unconstrained id="captured-information-card">
      <CardHeader compact bordered>
        <CardTitle cardLayout>
          Captured information
          {vm.showDraftView && (
            <CardHeaderActions>
              <Tooltip content="Edit responses">
                <IconButton
                  data-test="captured-information-card-edit-responses-button"
                  onClick={vm.editDraft}
                >
                  <Icon name="edit-outlined" size={20} />
                </IconButton>
              </Tooltip>
            </CardHeaderActions>
          )}
        </CardTitle>
        <div className={styles.tabs}>
          <TabBar>
            <TabItem
              cardLayout
              active={
                vm.detailsVM.capturedInformationTabController.current.tab ===
                Tab.Questions
              }
              onClick={() =>
                vm.detailsVM.capturedInformationTabController.change(
                  Tab.Questions,
                )
              }
              dataTest="questions-tab-item"
            >
              Questions
            </TabItem>
            {vm.notesVM.areNotesEnabled && (
              <TabItem
                cardLayout
                active={
                  vm.detailsVM.capturedInformationTabController.current.tab ===
                  Tab.Notes
                }
                onClick={() =>
                  vm.detailsVM.capturedInformationTabController.change(
                    Tab.Notes,
                  )
                }
                dataTest="notes-tab-item"
              >
                Notes
              </TabItem>
            )}
            {vm.personalProfileVM.personalInfoSupportingItem?.completed && (
              <TabItem
                cardLayout
                highlighted
                active={
                  vm.detailsVM.capturedInformationTabController.current.tab ===
                  Tab.PersonalInfo
                }
                onClick={() =>
                  vm.detailsVM.capturedInformationTabController.change(
                    Tab.PersonalInfo,
                  )
                }
                dataTest="personal-info-tab-item"
              >
                Personal
              </TabItem>
            )}

            {vm.businessInfoVM.businessSupportingItem?.completed && (
              <TabItem
                cardLayout
                highlighted
                active={
                  vm.detailsVM.capturedInformationTabController.current.tab ===
                  Tab.BusinessInfo
                }
                onClick={() =>
                  vm.detailsVM.capturedInformationTabController.change(
                    Tab.BusinessInfo,
                  )
                }
                dataTest="business-tab-item"
              >
                Business
              </TabItem>
            )}

            {vm.taxRefundMethodVM.isRelevant && (
              <TabItem
                cardLayout
                highlighted
                active={
                  vm.detailsVM.capturedInformationTabController.current.tab ===
                  Tab.RefundInfo
                }
                onClick={() =>
                  vm.detailsVM.capturedInformationTabController.change(
                    Tab.RefundInfo,
                  )
                }
                dataTest="refund-tab-item"
              >
                Refund
              </TabItem>
            )}
          </TabBar>
        </div>
      </CardHeader>
      <CardContent scroll listContent>
        {match(vm.detailsVM.capturedInformationTabController.current.tab, {
          [Tab.Questions]: () =>
            vm.detailsVM.fetchResponses.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading questions." error={err} />
              ),
              resolved: () =>
                vm.detailsVM.responses.questions.length > 0 ||
                vm.job.answers.length > 0 ? (
                  <QuestionsTab vm={vm.detailsVM.responses} />
                ) : (
                  <JobCardEmptyState
                    topPadding
                    helpText="Nothing to display."
                  />
                ),
            }),
          [Tab.Notes]: () =>
            vm.fetchChecklist.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading checklist." error={err} />
              ),
              resolved: () => <NotesTab vm={vm.notesVM} />,
            }),
          [Tab.BusinessInfo]: () =>
            vm.fetchChecklist.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading checklist." error={err} />
              ),
              resolved: () => <BusinessInfo vm={vm.businessInfoVM} />,
            }),
          [Tab.PersonalInfo]: () =>
            vm.fetchChecklist.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading checklist." error={err} />
              ),
              resolved: () => (
                <>
                  <PersonalProfile vm={vm.personalProfileVM} />
                  <SpousalProfile vm={vm.spousalProfileVM} />
                  <DependentsProfile vm={vm.dependentProfileVM} />
                </>
              ),
            }),
          [Tab.RefundInfo]: () =>
            vm.fetchChecklist.match({
              pending: () => <SectionLoading />,
              rejected: (err) => (
                <SectionError title="Error loading checklist." error={err} />
              ),
              resolved: () => <RefundInfo vm={vm.taxRefundMethodVM} />,
            }),
        })}
      </CardContent>
    </Card>
  )
})

interface QuestionItemProps<T extends QuestionDTO> {
  questionVM: QuestionViewModelFor<T>
}

function Question<T extends QuestionDTO>({ questionVM }: QuestionItemProps<T>) {
  return (
    <div className={styles.capturedInfoItem}>
      <div className={styles.capturedInfoLabel}>{questionVM.question}</div>
      <div className={styles.capturedInfoValue}>
        <ResponseValue vm={questionVM} />
      </div>
    </div>
  )
}

interface QuestionTabProps {
  vm: QuestionListViewModel
}

export const QuestionsTab = ({ vm }: QuestionTabProps) => {
  return (
    <div>
      {vm.questions.map((vm) => (
        <Question key={vm.id} questionVM={vm} />
      ))}
    </div>
  )
}

interface NotesTabProps {
  vm: NotesViewModel
}

export const NotesTab = observer(function NotesTab({ vm }: NotesTabProps) {
  const label = vm.isInfoGatheringStep ? 'Notes' : 'Instructions'
  const buttonLabel = vm.notesSupportingItemInput.text
    ? 'Edit notes'
    : 'Add notes'
  const requiredTag = vm.notesSupportingItem?.required && vm.isInfoGatheringStep
  return (
    <div className={styles.noteTabRootContainer}>
      <NotesDialog vm={vm.notesDialogVM} />
      <FormGroupContainer className={styles.noteTabTextAreaContainer}>
        <label className={styles.noteTabTextAreaLabel}>
          {label}{' '}
          {requiredTag && (
            <span className={styles.noteRequiredTag}>*Required</span>
          )}{' '}
          {!vm.isInfoGatheringStep && (
            <span className={styles.noteProvidedTag}>Provided</span>
          )}
        </label>
        <TextArea
          rows={5}
          minLength={1}
          placeholder="You don't have any instructions yet."
          value={vm.notesSupportingItemInput?.text}
          data-test="checklist-notes-description"
          disabled={true}
        />
      </FormGroupContainer>

      {vm.canEditNotes && (
        <Button
          size="wide-slim"
          type="secondary"
          data-test="checklist-notes-edit-button"
          onClick={vm.showNotesDialog}
        >
          {buttonLabel}
        </Button>
      )}
    </div>
  )
})
